import { Fragment, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import { Controller } from "react-hook-form";
import { clsx as cx } from "clsx";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../../Providers/ToastProvider";
import { describeInvalidFields } from "./LeadForm.errors";
import { collectAutofilledValues } from "./LeadForm.autofill";
import { filterCityOptions, shouldOpenCityList } from "./city-options";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { InputTextarea } from 'primereact/inputtextarea';

import { useLeadForm } from "./LeadForm.hooks";
import {
  CustomerCategoryEnum,
  LeadStatusEnum,
  RecurrenceEnum,
  TLeadFormData,
} from "../Lead.model";
import {
  customerCategoryOptions,
  petsOptions,
  recurrenceOptions,
} from "./config";

import styles from "./LeadForm.module.scss";
import { useUSAStates } from "../../../../helpers/helperHooks";
import { toDropdownLabels } from "../../../Employee/EmployeeForm/helpers";
import { countries } from "../../../../helpers/states";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import RecurrenceForm from "../../../../components/RecurrenceForm/RecurrenceForm";
import { If } from "../../../../components/If/If";
import Autocomplete from "react-google-autocomplete";
import GooglePlacesAutocomplete from "../../../../components/GooglePlacesAutocomplete";

type LeadFormProps = {
  onSubmit: (props: TLeadFormData) => void;
  leadId?: string;
  isPending: boolean;
  isEditForm?: boolean;
};

export const LeadForm = ({
  onSubmit,
  leadId,
  isPending,
  isEditForm = false,
}: LeadFormProps) => {
  const navigate = useNavigate();
  const { show } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  // PrimeReact's Dropdown exposes show()/hide() at runtime but its type
  // declaration only lists getElement/getOverlay, so the ref is typed to the
  // part we actually call.
  const cityRef = useRef<{ show: () => void } | null>(null);
  const billingCityRef = useRef<{ show: () => void } | null>(null);

  const [isBillingAddressChecked, setIsBillingAddressChecked] =
    useState<boolean>(false);
  const [hasAppliedExistingClient, setHasAppliedExistingClient] =
    useState(false);

  const states = useUSAStates();

  const {
    hookForm,
    projectTypeOptions,
    filteredScopeOfWorkOptions,
    floorSurfaceOptions,
    paperProductOptions,
    suppliesOptions,
    cities,
    billingCities,
    currentLead,
    existingClientLookup,
    applyExistingClientToLead,
  } = useLeadForm(leadId);
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    trigger,
  } = hookForm;

  const [customerCategory, recurrence, daysOfWeek, frequency] = watch([
    "customerCategory",
    "recurrence",
    "daysOfWeek",
    "frequency",
  ]);

  useEffect(() => {
    if (currentLead.data) {
      setValue("daysOfWeek", currentLead.data.daysOfWeek);
    }
  }, [currentLead.data]);

  const getFormErrorMessage = (name: any) => {
    return (
      // ✅ THIS IS OK
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      errors[name] && <small className="p-error">{errors[name].message}</small>
    );
  };

  const handleWeekDayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const daysSet = new Set(daysOfWeek);
    //@ts-ignore
    const dayValue = +e.target.getAttribute("data-day-index");
    if (daysSet.has(dayValue)) {
      daysSet.delete(dayValue);
    } else {
      daysSet.add(dayValue);
    }

    //@ts-ignore
    const daysArr = [...daysSet];
    setValue("daysOfWeek", [...daysArr]);
  };

  const handleBillingAddressCheckbox = (e: any) => {
    if (e.checked) {
      setValue("billingAddress", getValues("address"));
      setValue("billingState", getValues("state"));
      setValue("billingCity", getValues("city"));
      setValue("billingZIP", getValues("ZIP"));
      setValue("billingStreet", "");
    } else {
      setValue("billingAddress", "");
      setValue("billingState", "");
      setValue("billingCity", "");
      setValue("billingZIP", "");
      setValue("billingStreet", "");
    }

    setIsBillingAddressChecked(e.checked);
  };

  const handlePlaceSelect = (place: any) => {
    const address = place.formatted_address;
    // shouldValidate clears a "Field is required" that is already on screen -
    // setValue alone leaves the stale error showing over a filled-in field.
    const applied = { shouldValidate: true, shouldDirty: true };

    setValue("address", address, applied);

    let zipCode, state, city;
    // Submitting free text instead of picking a suggestion yields a place with
    // no address_components; that used to throw and abandon the rest of this.
    (place.address_components ?? []).forEach((component: any) => {
      if (component.types.includes("postal_code")) {
        zipCode = component.long_name;
      } else if (component.types.includes("administrative_area_level_1")) {
        state = component.long_name;
      } else if (component.types.includes("locality")) {
        city = component.long_name;
      }
    });

    if (zipCode) {
      setValue("ZIP", zipCode, applied);
    }

    if (state) {
      setValue("state", state, applied);
    }

    if (city) {
      setValue("city", city, applied);
    }
  };

  return (
    <form
      ref={formRef}
      onSubmit={(event) => {
        // Pull in anything the browser filled that never reached the form, so a
        // prefilled form validates the same as a typed one.
        collectAutofilledValues(formRef.current, (field) =>
          getValues(field as any)
        ).forEach(([field, value]) =>
          setValue(field as any, value, { shouldValidate: true, shouldDirty: true })
        );

        return handleSubmit(onSubmit, (formErrors) => {
          // Without this a failed validation is completely silent: the form
          // does not focus the first invalid field, and an error below the
          // fold is easy to miss, so Save looks like it does nothing.
          const invalid = describeInvalidFields(formErrors);

          show({
            severity: "error",
            detail: invalid
              ? `Check these fields before saving: ${invalid}`
              : "Some fields still need attention before saving.",
            life: 8000,
          });
        })(event);
      }}
      className={cx(styles.form)}
    >
      <div className={styles.fields}>
        <div className={styles.fieldGroup}>
          <div className={styles.content}>
            <div className={styles.field}>
              <LabelValue label="Type of project" isRequired />
              <Controller
                name="contractTypeId"
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    appendTo="self"
                    tabIndex={1}
                    options={projectTypeOptions}
                    ariaLabel="name"
                    placeholder="Select a Type"
                    className={cx(styles.dropdown, {
                      "p-invalid": errors.contractTypeId,
                    })}
                    onChange={(e) => {
                      if (e.value !== field.value) {
                        setValue("scopeOfWorkIds", []);
                      }
                      field.onChange(e);
                    }}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("contractTypeId")}
            </div>
            <div className={styles.field}>
              <LabelValue label="Date Added to System" />
              <Controller
                name="createdDate"
                control={control}
                render={({ field }) => (
                  <Calendar
                    id={field.name}
                    tabIndex={2}
                    value={field.value || new Date()}
                    onChange={(e) => field.onChange(e.value)}
                    placeholder="MM/DD/YY"
                    dateFormat="mm/dd/yy"
                    maxDate={new Date()}
                    showIcon
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <h4>CUSTOMER CONTACT INFORMATION</h4>
          {hasAppliedExistingClient ? (
            <div className={styles.prefillNotice}>
              Existing client details were applied. Confirm the building address before saving.
            </div>
          ) : null}
          <div className={styles.content}>
            <div className={styles.fieldsColumn}>
              <div className={styles.field}>
                <LabelValue label="Customer Category" isRequired />
                <Controller
                  name="customerCategory"
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      tabIndex={3}
                      appendTo="self"
                      onChange={(option) => field.onChange(option.value)}
                      options={customerCategoryOptions}
                      ariaLabel="customerCategory"
                      placeholder="Select a Type"
                      className={cx(styles.dropdown, {
                        "p-invalid": errors.customerCategory,
                      })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("customerCategory")}
              </div>
              <div className={styles.field}>
                <LabelValue label="Country" isRequired />
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      appendTo="self"
                      id={field.name}
                      tabIndex={7}
                      value={field.value}
                      // onChange={(e) => field.onChange(e.value)}
                      options={toDropdownLabels(countries)}
                      placeholder="Country"
                      style={{ width: "100%" }}
                      // className={cx({
                      //   "p-invalid": errors.country,
                      // })}
                      disabled={true}
                    />
                  )}
                />
                {getFormErrorMessage("country")}
              </div>
              <div className={styles.field}>
                <LabelValue label="ZIP" isRequired />
                <Controller
                  name="ZIP"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      keyfilter="int"
                      tabIndex={9}
                      placeholder="ZIP"
                      className={cx({ "p-invalid": errors.ZIP })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("ZIP")}
              </div>
              <div className={styles.field}>
                <LabelValue label="Fax" />
                <Controller
                  name="fax"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={12}
                      placeholder="Fax"
                      className={cx({ "p-invalid": errors.fax })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("fax")}
              </div>
              {customerCategory === CustomerCategoryEnum.CORPORATE ? (
                <Fragment key="corporate">
                  <div className={styles.field}>
                    <LabelValue label="Company name" isRequired />
                    <Controller
                      name="companyName"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          tabIndex={15}
                          placeholder="Company name"
                          className={cx(styles.dropdown, {
                            "p-invalid": errors.companyName,
                          })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("companyName")}
                  </div>
                  <div className={styles.field}>
                    <LabelValue label="Contact Name" isRequired />
                    <Controller
                      name="contactName"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          tabIndex={16}
                          placeholder="Contact Name"
                          className={cx(styles.dropdown, {
                            "p-invalid": errors.contactName,
                          })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("contactName")}
                  </div>
                </Fragment>
              ) : (
                <Fragment key="individual">
                  <div className={styles.field}>
                    <LabelValue label="First Name" isRequired />
                    <Controller
                      name="name"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          tabIndex={15}
                          placeholder="First Name"
                          className={cx(styles.dropdown, {
                            "p-invalid": errors.name,
                          })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("name")}
                  </div>
                  <div className={styles.field}>
                    <LabelValue label="Last Name" isRequired />
                    <Controller
                      name="surname"
                      render={({ field }) => (
                        <InputText
                          {...field}
                          tabIndex={16}
                          placeholder="Last Name"
                          className={cx(styles.dropdown, {
                            "p-invalid": errors.name,
                          })}
                        />
                      )}
                      control={control}
                    />
                    {getFormErrorMessage("name")}
                  </div>
                </Fragment>
              )}
            </div>
            <div className={styles.fieldsColumn}>
              <div className={styles.field}>
                <LabelValue label="Title" />
                <Controller
                  name="title"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder="Title"
                      tabIndex={4}
                      className={cx({ "p-invalid": errors.title })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("title")}
              </div>
              <div className={styles.field}>
                <LabelValue label="State" isRequired />
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      appendTo="self"
                      id={field.name}
                      tabIndex={7}
                      value={field.value}
                      onChange={(e) => {
                        setValue("city", "");
                        field.onChange(e.value);
                      }}
                      options={states}
                      placeholder="State"
                      style={{ width: "100%" }}
                      className={cx({
                        "p-invalid": errors.state,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage("state")}
              </div>
              <div className={styles.field}>
                <LabelValue label="Address" isRequired />

                <Controller
                  name="address"
                  render={({ field }) => (
                    <GooglePlacesAutocomplete
                      onPlaceSelected={handlePlaceSelect}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                      errors={errors}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("address")}
              </div>

              <div className={styles.field}>
                <LabelValue label="Phone" isRequired />
                <Controller
                  name="phone"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={13}
                      placeholder="Phone"
                      className={cx({ "p-invalid": errors.phone })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("phone")}
              </div>

            </div>
            <div className={styles.fieldsColumn}>
              <div className={styles.field}>
                <LabelValue label="Email" isRequired />

                <Controller
                  name="email"
                  render={({ field }) => (
                    <InputText
                      name={field.name}
                      ref={field.ref}
                      value={field.value}
                      onChange={(event) => {
                        setHasAppliedExistingClient(false);
                        field.onChange(event);
                      }}
                      onBlur={async () => {
                        field.onBlur();
                        const email = getValues("email");
                        setHasAppliedExistingClient(false);
                        if (!email || leadId) return;
                        const isEmailValid = await trigger("email");
                        if (!isEmailValid) return;
                        const requestedEmail = email.trim();
                        const requestedEmailKey = requestedEmail.toLowerCase();
                        const client = await existingClientLookup.mutateAsync(requestedEmail);
                        const currentEmailKey = getValues("email")?.trim().toLowerCase();
                        if (currentEmailKey !== requestedEmailKey || !client) return;
                        applyExistingClientToLead(client);
                        setHasAppliedExistingClient(true);
                      }}
                      tabIndex={5}
                      // disabled={isEditForm}
                      disabled={
                        currentLead.data &&
                        currentLead.data.status === LeadStatusEnum.Approved
                      }
                      placeholder="Email"
                      className={cx({ "p-invalid": errors.email })}
                    />
                  )}
                  control={control}
                />

                {getFormErrorMessage("email")}
              </div>
              <div className={styles.field}>
                <LabelValue label="City" isRequired />
                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      appendTo="self"
                      id={field.name}
                      tabIndex={8}
                      value={field.value}
                      ref={cityRef as any}
                      onChange={(e) => {
                        field.onChange(e.value);
                        if (shouldOpenCityList(cities, e.value)) {
                          cityRef.current?.show();
                        }
                      }}
                      options={filterCityOptions(cities, field.value)}
                      editable
                      placeholder="City"
                      style={{ width: "100%" }}
                      className={cx({
                        "p-invalid": errors.city,
                      })}
                    />
                  )}
                />
                {getFormErrorMessage("city")}
              </div>
              <div className={styles.field}>
                <LabelValue label="Website" />
                <Controller
                  name="website"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder="Website"
                      tabIndex={11}
                      className={cx({ "p-invalid": errors.website })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("website")}
              </div>
              <div className={styles.field}>
                <LabelValue label="Mobile" />
                <Controller
                  name="mobile"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      tabIndex={14}
                      placeholder="Mobile"
                      className={cx({ "p-invalid": errors.mobile })}
                    />
                  )}
                  control={control}
                />
                {getFormErrorMessage("mobile")}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <h4>BILLING INFORMATION</h4>
          <div className={styles.sameAsAddress}>
            <Checkbox
              inputId="sameAsAddress"
              name="sameAsAddress"
              tabIndex={18}
              onChange={handleBillingAddressCheckbox}
              checked={isBillingAddressChecked}
            />
            <label htmlFor="sameAsAddress">Same as Address</label>
          </div>
          <div className={styles.content}>
            <div className={styles.field}>
              <LabelValue label="Billing contact" isRequired />
              <Controller
                name="billingContact"
                render={({ field }) => (
                  <InputText
                    {...field}
                    tabIndex={19}
                    placeholder="Billing contact"
                    className={cx({ "p-invalid": errors.billingContact })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("billingContact")}
            </div>
            <div className={styles.field}>
              <LabelValue label="Deposit amount" />
              <Controller
                name="depositAmount"
                render={({ field }) => (
                  <InputNumber
                    value={field.value}
                    tabIndex={20}
                    onValueChange={(e) => field.onChange(e.value)}
                    placeholder="Deposit amount"
                    inputId="currency-us"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    className={cx(styles.fullWidth, {
                      "p-invalid": errors.depositAmount,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("depositAmount")}
            </div>
            <div className={styles.field}>
              <LabelValue label="Billing Address" />
              <Controller
                name="billingAddress"
                render={({ field }) => (
                  <InputText
                    {...field}
                    tabIndex={21}
                    placeholder="Billing Address"
                    className={cx({
                      "p-invalid": errors.billingCity,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("billingState")}
            </div>

            <div className={styles.field}>
              <LabelValue label="State" isRequired />
              <Controller
                name="billingState"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    appendTo="self"
                    id={field.name}
                    tabIndex={22}
                    value={field.value}
                    onChange={(e) => {
                      setValue("billingCity", "");
                      field.onChange(e.value);
                    }}
                    options={states}
                    placeholder="State"
                    style={{ width: "100%" }}
                    className={cx({
                      "p-invalid": errors.billingCity,
                    })}
                  />
                )}
              />
              {getFormErrorMessage("billingState")}
            </div>
            <div className={styles.field}>
              <LabelValue label="City" isRequired />
              <Controller
                name="billingCity"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    appendTo="self"
                    id={field.name}
                    tabIndex={23}
                    value={field.value}
                    ref={billingCityRef as any}
                    onChange={(e) => {
                      field.onChange(e.value);
                      if (shouldOpenCityList(billingCities, e.value)) {
                        billingCityRef.current?.show();
                      }
                    }}
                    options={filterCityOptions(billingCities, field.value)}
                    editable
                    placeholder="City"
                    style={{ width: "100%" }}
                    className={cx({
                      "p-invalid": errors.billingCity,
                    })}
                  />
                )}
              />
              {getFormErrorMessage("billingCity")}
            </div>
            <div className={styles.field}>
              <LabelValue label="ZIP" isRequired />
              <Controller
                name="billingZIP"
                render={({ field }) => (
                  <InputText
                    {...field}
                    keyfilter="int"
                    tabIndex={24}
                    placeholder="ZIP"
                    className={cx({ "p-invalid": errors.billingZIP })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("billingZIP")}
            </div>
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <div className={styles.content}>
            <div className={styles.field}>
              <LabelValue label="Estimated start date" />
              <Controller
                name="estimatedStartDate"
                control={control}
                render={({ field }) => (
                  <Calendar
                    id={field.name}
                    value={field.value}
                    tabIndex={34}
                    onChange={(e) => field.onChange(e.value)}
                    placeholder="MM/DD/YY"
                    dateFormat="mm/dd/yy"
                    showIcon
                    minDate={new Date()}
                    className={cx(styles.fullWidth, {
                      "p-invalid": errors.estimatedStartDate,
                    })}
                  />
                )}
              />
              {getFormErrorMessage("estimatedStartDate")}
            </div>

            <div className={styles.field}>
              <LabelValue label="Labor goal - hours" isRequired />
              <Controller
                name="laborGoalHours"
                render={({ field }) => (
                  <InputText
                    {...field}
                    keyfilter="pint"
                    tabIndex={35}
                    placeholder="Labor goal - hours"
                    className={cx(styles.fullWidth, {
                      "p-invalid": errors.laborGoalHours,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("laborGoalHours")}
            </div>

            <div className={styles.field}>
              <LabelValue label="Estimated SQFT" isRequired />
              <Controller
                name="estimatedSQFT"
                render={({ field }) => (
                  <InputText
                    {...field}
                    keyfilter="pint"
                    tabIndex={35}
                    placeholder="Estimated SQFT"
                    className={cx(styles.fullWidth, {
                      "p-invalid": errors.estimatedSQFT,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("laborGoalHours")}
            </div>




            <div className={styles.field}>
              <LabelValue label="Budget" isRequired />
              <Controller
                name="laborGoalBudget"
                render={({ field }) => (
                  <InputNumber
                    value={field.value}
                    onValueChange={(e) => field.onChange(e.value)}
                    inputId="currency-us"
                    mode="currency"
                    tabIndex={36}
                    currency="USD"
                    locale="en-US"
                    placeholder="Budget"
                    className={cx(styles.fullWidth, {
                      "p-invalid": errors.laborGoalBudget,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("laborGoalBudget")}
            </div>

            <div className={styles.field}>
              <LabelValue label="Describe what the customer needs" isRequired />
              <Controller
                name="specialInstruction"
                render={({ field }) => (
                  <InputTextarea
                    {...field}

                    tabIndex={35}
                    placeholder=""
                    className={cx(styles.fullWidth, {
                      "p-invalid": errors.laborGoalHours,
                    })}
                  />
                )}
                control={control}
              />
              {getFormErrorMessage("laborGoalHours")}
            </div>


          </div>
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          label="Cancel"
          type="reset"
          tabIndex={37}
          className="p-button-outlined p-button-secondary"
          onClick={() => navigate("/sales/leads")}
        />
        <Button
          label={leadId ? "Update" : "Save"}
          type="submit"
          tabIndex={38}
          disabled={isPending}
        />
      </div>
    </form>
  );
};
